import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import {
  CartRowFeatureOptionSelection,
  FeatureOptionType,
  ProductConfiguration,
  ProductConfigurationFeature,
  ProductConfigurationOption,
  RuleActionType
} from '../../../models/product-configuration-types';
import { first, forkJoin, Observable, of, Subject, switchMap, takeUntil } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { GungNotificationService } from 'gung-common';
import { ProductConfigurationService } from '../../../services/cpq/product-configuration.service';
import { ProductConfigurationRuleExecutionService } from '../../../services/cpq/product-configuration-rule-execution.service';
import { ProductService } from '../../../services/products/product.service';
import { CartService } from '../../../services/cart/cart.service';
import { Product } from '../../../models';
import {
  OptionPrice,
  ProductConfigurationOptionPriceService
} from '../../../services/cpq/product-configuration-option-price.service';
import { ProductConfigurationForcedOptionService } from '../../../services/cpq/product-configuration-forced-option.service';
import { CpqConfigService } from '../../../services/cpq/cpq-config.service';
import { CartRow } from '../../../state/cart/types';

@Component({
  selector: 'lib-cpq-configurator',
  templateUrl: './cpq-configurator.component.html',
  styleUrls: ['./cpq-configurator.component.scss'],
  standalone: false
})
export class CpqConfiguratorComponent implements OnInit, OnDestroy {
  featureResults: Map<string, ProductConfigurationOption[]>;
  groupedFeatures: Map<string, ProductConfigurationFeature[]>;
  groupedFeaturesArray: Array<{ key: string; value: ProductConfigurationFeature[] }> = [];
  keyedOptions: Map<string, ProductConfigurationOption>;
  keyedFeatures: Map<string, ProductConfigurationFeature>;
  featureOptions: Map<string, ProductConfigurationOption[]>;

  disabledOptions = new Map<string, boolean>();
  hiddenOptions = new Map<string, boolean>();

  protected optionPriceService = inject(ProductConfigurationOptionPriceService);
  optionPriceMap = new Map<string, OptionPrice>();

  protected forcedOptionService = inject(ProductConfigurationForcedOptionService);
  // Option ids that are mandatory for the currently selected customer (e.g. compliance material). These always
  // win over the ruleset: a forced option is always visible, selected and locked — no rule can hide or deselect it.
  forcedOptionIds = new Set<string>();

  featureNgModels = {};

  protected cpqConfigService = inject(CpqConfigService);
  quantity = 1;
  protected editedCartRow: CartRow | undefined;
  pillSelections: { [featureId: string]: { [property: string]: string } } = {};
  materialProducts = new Map<string, Product>();

  @Input()
  productId: string;

  @Input()
  configurationOutputProductId: string | undefined;

  product: Product;

  configuration: ProductConfiguration;

  isLoading = true;

  private unsubscribe = new Subject<void>();
  protected executeRuleSubject = new Subject<void>();

  constructor(
    protected route: ActivatedRoute,
    protected productService: ProductService,
    protected productConfigurationService: ProductConfigurationService,
    protected ruleExecutionService: ProductConfigurationRuleExecutionService,
    protected cartService: CartService,
    protected gungNotificationService: GungNotificationService
  ) {}

  ngOnInit(): void {
    this.productId = this.getProductId();
    if (!this.productId) {
      return;
    }

    // Initialize the rule execution subscription.
    // Each time we select an option, this subscription will fire and get
    // the updated results.
    this.initRuleExecutionSubscription();

    this.featureResults = new Map<string, any[]>();

    this.productConfigurationService
      .getOne(this.productId)
      .pipe(
        first(),
        switchMap(configuration => {
          const featureIds = [];
          const optionIds = [];
          for (const feature of configuration.features) {
            featureIds.push(feature.featureId);
            optionIds.push(...feature.optionIds);
          }

          return forkJoin([
            of(configuration),
            this.productConfigurationService.getFeatures(featureIds).pipe(first()),
            this.productConfigurationService.getOptions(optionIds).pipe(first()),
            this.productService.getProduct(this.productId).pipe(first())
          ]);
        }),
        switchMap(([configuration, features, options, product]) => {
          const allOptionIds = options.map(o => o.id);
          return forkJoin([
            this.optionPriceService.fetchOptionPrices(allOptionIds).pipe(first()),
            this.forcedOptionService.fetchForcedOptionIds(allOptionIds).pipe(first()),
            this.fetchMaterialProducts(options)
          ]).pipe(
            switchMap(
              ([priceMap, forcedIds, materialProducts]) =>
                of([configuration, features, options, product, priceMap, forcedIds, materialProducts] as const)
            )
          );
        })
      )
      .subscribe(([configuration, features, options, product, priceMap, forcedIds, materialProducts]) => {
        this.configuration = configuration;
        this.groupedFeatures = this.groupFeatures(features);
        this.groupedFeaturesArray = Array.from(this.groupedFeatures.entries()).map(([key, value]) => ({ key, value }));
        this.keyedOptions = this.keyOptions(options);
        this.keyedFeatures = this.keyFeatures(features);
        this.featureOptions = this.groupOptionsByFeature(options);
        this.optionPriceMap = priceMap;
        this.forcedOptionIds = forcedIds;
        this.materialProducts = new Map(materialProducts.map(p => [p.id, p]));
        this.initNgModels(features.map(f => f.id));
        this.executeRuleSubject.next();

        this.isLoading = false;
        this.applyDefaultSelection();
        this.restoreSelectionFromCart();
      });
  }

  protected fetchMaterialProducts(options: ProductConfigurationOption[]): Observable<Product[]> {
    if (!this.cpqConfigService.loadMaterialProducts()) {
      return of([]);
    }
    const ids = [...new Set(options.flatMap(option => (option.materials || []).map(m => m.productId)))];
    return ids.length > 0 ? this.productService.getProductsByIds(ids).pipe(first()) : of([]);
  }

  protected applyDefaultSelection(): void {
    if (this.route.snapshot.queryParamMap.get('config')) {
      return;
    }
    const defaults = this.cpqConfigService.getDefaultOptionIds(
      this.configuration,
      [...this.keyedFeatures.values()],
      [...this.keyedOptions.values()]
    );
    for (const optionId of defaults) {
      const option = this.keyedOptions.get(optionId);
      const feature = option && this.keyedFeatures.get(option.featureId);
      if (!feature) {
        continue;
      }
      if (feature.featureOptionType !== FeatureOptionType.ADD_ON) {
        this.featureNgModels[feature.id] = option.id;
      }
      this.featureOptionSelected(feature, option);
    }
  }

  getOptionLabel(feature: ProductConfigurationFeature, option: ProductConfigurationOption): string {
    return this.cpqConfigService.getOptionLabel(feature, option, this.materialProducts);
  }

  getOptionSubLabel(feature: ProductConfigurationFeature, option: ProductConfigurationOption): string | undefined {
    return this.cpqConfigService.getOptionSubLabel(feature, option, this.materialProducts);
  }

  protected restoreSelectionFromCart(): void {
    const partialId = this.route.snapshot.queryParamMap.get('config');
    if (!partialId || !this.cpqConfigService.getEditConfigurationRoute()) {
      return;
    }
    this.cartService
      .getCurrentCart()
      .pipe(first())
      .subscribe(rows => {
        const row = rows.find(r => r.productPartialId === partialId);
        if (!row) {
          return;
        }
        this.editedCartRow = row;
        this.quantity = row.qty;
        for (const selection of row.extra?.productConfigurationSelectedOptions || []) {
          const feature = this.keyedFeatures.get(selection.feature?.id);
          const option = this.keyedOptions.get(selection.option?.id);
          if (!feature || !option) {
            continue;
          }
          if (feature.featureOptionType !== FeatureOptionType.ADD_ON) {
            this.featureNgModels[feature.id] = option.id;
          }
          this.featureOptionSelected(feature, option);
        }
      });
  }

  protected initRuleExecutionSubscription() {
    this.executeRuleSubject.pipe(takeUntil(this.unsubscribe)).subscribe(undefined => {
      this.removeUnavailableSelections();
      // Whenever we get notified that a selection has been made through the subject, we should execute the rules on the
      // new selection in order to see if anything has been changed according to the ruleset.
      this.ruleExecutionService
        .executeRules(this.productId, this.buildSelection())
        .pipe(first())
        .subscribe(ruleResults => {
          // This is done in a separate loop to ensure that we can still operate on other options than the one currently
          // in iteration without having to depend on the loop order. For example, we might want to disable all other
          // options for a specific feature. If we do this in one loop, we might iterate over one of those other options
          // after we mark them as disabled, and would overwrite that with the default.
          for (const optionId of Object.keys(ruleResults)) {
            // Set defaults on all options. Will be overwritten in the next step if needed.
            this.hiddenOptions.set(optionId, false);
            this.disabledOptions.set(optionId, false);
          }

          for (const optionId of Object.keys(ruleResults)) {
            // Options forced for the selected customer are handled below and must not be touched by the
            // ruleset (otherwise a HIDE/SHOW_AND_DISABLE rule could hide or deselect a mandatory option).
            if (this.forcedOptionIds.has(optionId)) {
              continue;
            }

            const value = ruleResults[optionId];

            const featureId = this.configuration.features.find(f => {
              return f.optionIds.indexOf(optionId) >= 0;
            }).featureId;

            if (value == RuleActionType.HIDE) {
              this.hiddenOptions.set(optionId, true);
              // Ensure that we remove the option from the current selection/ngmodel if it is selected previously.
              this.cleanupOptionFromSelections(featureId, optionId);
            } else if (value == RuleActionType.SHOW_AND_DISABLE) {
              this.disabledOptions.set(optionId, true);
              // Ensure that we remove the option from the current selection/ngmodel if it is selected previously.
              this.cleanupOptionFromSelections(featureId, optionId);
            } else if (value == RuleActionType.SHOW) {
              this.hiddenOptions.set(optionId, false);
            } else if (value == RuleActionType.SHOW_AND_SELECT) {
              this.featureNgModels[featureId] = optionId;

              const feature = this.keyedFeatures.get(featureId);
              // For single-select features (RADIO/SELECT), disable the other options since they are not possible
              // to select because of this auto select. For ADD_ON features, allow other options to remain selectable.
              if (feature?.featureOptionType !== FeatureOptionType.ADD_ON) {
                this.configuration.features
                  .find(f => f.featureId == featureId)
                  .optionIds.forEach(id => this.disabledOptions.set(id, true));
                // Make sure to enable the current option again.
                this.disabledOptions.set(optionId, false);
              }

              // IMPORTANT!!!
              // It is easy to cause an infinity loop here. We only want to run this when the option is not already
              // selected. Otherwise, it will select itself and rerun the rules every time.
              if (!this.isOptionSelected(featureId, optionId)) {
                this.featureOptionSelected(this.keyedFeatures.get(featureId), this.keyedOptions.get(optionId));
              }
            }
          }

          // Apply customer-forced options last so they override the ruleset. A forced option is always visible,
          // selected and LOCKED: for single-select features the whole feature is locked to it, for ADD_ON
          // features the option itself stays ticked and untickable while siblings remain free.
          this.forcedOptionIds.forEach(optionId => {
            const connectedFeature = this.configuration.features.find(f => f.optionIds.indexOf(optionId) >= 0);
            if (!connectedFeature) {
              return;
            }
            const featureId = connectedFeature.featureId;
            this.hiddenOptions.set(optionId, false);
            this.featureNgModels[featureId] = optionId;

            const feature = this.keyedFeatures.get(featureId);
            if (feature?.featureOptionType !== FeatureOptionType.ADD_ON) {
              connectedFeature.optionIds.forEach(id => this.disabledOptions.set(id, true));
            }
            // Unlike SHOW_AND_SELECT the forced option itself stays disabled — it cannot be deselected.
            this.disabledOptions.set(optionId, true);

            // Same infinite-loop guard as SHOW_AND_SELECT: only select when not already selected.
            if (!this.isOptionSelected(featureId, optionId)) {
              this.featureOptionSelected(feature, this.keyedOptions.get(optionId));
            }
          });

          this.hiddenOptions = new Map<string, boolean>(this.hiddenOptions);
          this.disabledOptions = new Map<string, boolean>(this.disabledOptions);
          this.featureNgModels = { ...this.featureNgModels };
        });
    });
  }

  private cleanupOptionFromSelections(featureId: string, optionId: string): void {
    if (this.featureNgModels[featureId] == optionId) {
      delete this.featureNgModels[featureId];
    }
    this.featureOptionRemoved(this.keyedFeatures.get(featureId), this.keyedOptions.get(optionId));
  }

  private isOptionSelected(featureId: string, optionId: string): boolean {
    if (!this.featureResults.has(featureId)) {
      return false;
    }
    return this.featureResults.get(featureId).findIndex(o => o.id == optionId) >= 0;
  }

  getProductId(): string {
    if (this.productId) {
      return this.productId;
    }
    return this.route.snapshot.paramMap.get('id');
  }

  protected groupFeatures(features: ProductConfigurationFeature[]): Map<string, any[]> {
    const result = new Map<string, ProductConfigurationFeature[]>();
    for (const feature of features.filter(f => this.isFeatureVisible(f))) {
      let groupId = feature.properties?.featureGroup || 'GENERAL';
      groupId = '' + groupId;
      if (!result.has(groupId)) {
        result.set(groupId, []);
      }
      result.get(groupId).push(feature);
    }
    return result;
  }

  protected keyOptions(options: ProductConfigurationOption[]): Map<string, ProductConfigurationOption> {
    const result = new Map<string, ProductConfigurationOption>();
    for (const option of options) {
      result.set(option.id, option);
    }
    return result;
  }

  protected keyFeatures(features: ProductConfigurationFeature[]): Map<string, ProductConfigurationFeature> {
    const result = new Map<string, ProductConfigurationFeature>();
    features.forEach(f => result.set(f.id, f));
    return result;
  }

  protected initNgModels(featureIds: string[]): void {
    for (const id of featureIds) {
      this.featureNgModels[id] = undefined;
    }
  }

  protected groupOptionsByFeature(options: ProductConfigurationOption[]): Map<string, ProductConfigurationOption[]> {
    const result = new Map<string, any[]>();
    for (const option of options) {
      if (!result.has(option.featureId)) {
        result.set(option.featureId, []);
      }
      result.get(option.featureId).push(option);
    }
    return result;
  }

  getPriceForOption(feature: ProductConfigurationFeature, option: ProductConfigurationOption): number {
    return this.optionPriceMap.get(option.id)?.price || 0;
  }

  getCurrencyForOption(feature: ProductConfigurationFeature, option: ProductConfigurationOption): string {
    return this.optionPriceMap.get(option.id)?.currency || '';
  }

  featureOptionSelected(feature: ProductConfigurationFeature, option: ProductConfigurationOption) {
    if (feature.featureOptionType == FeatureOptionType.ADD_ON) {
      this.featureOptionMultipleSelected(feature, option);
      return;
    }
    if (feature.featureOptionType == FeatureOptionType.NUMBER) {
      this.featureOptionNumberSelected(feature, option);
      return;
    }

    if (feature.featureOptionType == FeatureOptionType.RADIO) {
      this.featureNgModels[feature.id] = option.id;
    }

    this.featureResults.set(feature.id, [option]);
    // To refresh the ngModels
    this.featureResults = new Map<string, ProductConfigurationOption[]>(this.featureResults);
    this.executeRuleSubject.next();
  }

  private featureOptionMultipleSelected(feature: ProductConfigurationFeature, option: ProductConfigurationOption) {
    if (this.featureResults.has(feature.id)) {
      this.featureResults.set(feature.id, [...this.featureResults.get(feature.id), option]);
    } else {
      this.featureResults.set(feature.id, [option]);
    }
    // To refresh the ngModels
    this.featureResults = new Map<string, ProductConfigurationOption[]>(this.featureResults);
    this.executeRuleSubject.next();
  }

  private featureOptionNumberSelected(feature: ProductConfigurationFeature, option: ProductConfigurationOption) {
    const quantity = option.properties.quantityAdded || 0;
    if (quantity == 0) {
      this.featureOptionRemoved(feature, option);
      return;
    }

    if (this.featureResults.has(feature.id)) {
      // We already have added a selection for this feature. Now we need to check some things.
      // If we have selected the same option again, we should just update the quantity.
      // else we can insert it as a new option.
      const indexOfOption = this.featureResults.get(feature.id).findIndex(o => o.id == option.id);
      if (indexOfOption > -1) {
        // remove the old option
        this.featureResults.get(feature.id).splice(indexOfOption, 1);
      }
      this.featureResults.set(feature.id, [...this.featureResults.get(feature.id), option]);
    } else {
      this.featureResults.set(feature.id, [option]);
    }

    this.featureResults = new Map<string, ProductConfigurationOption[]>(this.featureResults);
    this.executeRuleSubject.next();
  }

  featureOptionRemoved(feature: ProductConfigurationFeature, option: ProductConfigurationOption) {
    if (!this.featureResults.has(feature.id)) {
      return;
    }

    const filtered = this.featureResults.get(feature.id).filter(o => o.id != option.id);
    this.featureResults.set(feature.id, filtered);
    // To refresh the ngModels
    this.featureResults = new Map<string, ProductConfigurationOption[]>(this.featureResults);
  }

  getFilteredOptions(feature: ProductConfigurationFeature): ProductConfigurationOption[] {
    return this.cpqConfigService.getAvailableOptions(feature, this.featureOptions.get(feature.id), this.featureResults);
  }

  protected removeUnavailableSelections(): void {
    for (const [featureId, selected] of this.featureResults.entries()) {
      const feature = this.keyedFeatures.get(featureId);
      const available = feature ? this.getFilteredOptions(feature) : undefined;
      if (!available) {
        continue;
      }
      selected
        .filter(option => !available.some(a => a.id === option.id))
        .forEach(option => this.cleanupOptionFromSelections(featureId, option.id));
    }
  }

  usesPills(feature: ProductConfigurationFeature): boolean {
    if (feature.properties?.featurePresentation !== 'pills') {
      return false;
    }
    const options = this.getFilteredOptions(feature) || [];
    const properties = this.cpqConfigService.getPillProperties();
    return options.length > 0 && options.every(option => properties.every(p => !!this.getOptionProperty(option, p)));
  }

  getPillValues(feature: ProductConfigurationFeature, property: string): string[] {
    const values = (this.getFilteredOptions(feature) || [])
      .filter(option => !this.hiddenOptions.get(option.id))
      .map(option => this.getOptionProperty(option, property));
    return [...new Set(values)].sort((a, b) => Number(a) - Number(b) || a.localeCompare(b));
  }

  isPillValueSelected(feature: ProductConfigurationFeature, property: string, value: string): boolean {
    const selected = this.featureResults.get(feature.id)?.[0];
    return (this.pillSelections[feature.id]?.[property] ?? (selected && this.getOptionProperty(selected, property))) === value;
  }

  /**
   * A value is available when some offered option has it together with the values already chosen for the other
   * properties, e.g. height 240 is unavailable while width 147 only exists as 147x318.
   */
  isPillValueAvailable(feature: ProductConfigurationFeature, property: string, value: string): boolean {
    const chosen = { ...this.getPillsOf(this.featureResults.get(feature.id)?.[0]), ...this.pillSelections[feature.id] };
    delete chosen[property];
    return (this.getFilteredOptions(feature) || []).some(
      option =>
        !this.hiddenOptions.get(option.id) &&
        !this.disabledOptions.get(option.id) &&
        this.getOptionProperty(option, property) === value &&
        Object.keys(chosen).every(p => !chosen[p] || this.getOptionProperty(option, p) === chosen[p])
    );
  }

  togglePill(feature: ProductConfigurationFeature, property: string, value: string): void {
    if (this.isPillValueSelected(feature, property, value)) {
      const selected = this.featureResults.get(feature.id)?.[0];
      this.pillSelections[feature.id] = { ...this.getPillsOf(selected), ...this.pillSelections[feature.id], [property]: '' };
    } else {
      this.selectPill(feature, property, value);
    }
  }

  protected hasClearedPill(): boolean {
    return Object.values(this.pillSelections).some(pills => Object.values(pills).some(value => value === ''));
  }

  selectPill(feature: ProductConfigurationFeature, property: string, value: string): void {
    const selected = this.featureResults.get(feature.id)?.[0];
    const wanted = { ...this.getPillsOf(selected), ...this.pillSelections[feature.id], [property]: value };
    const candidates = (this.getFilteredOptions(feature) || []).filter(
      option =>
        !this.hiddenOptions.get(option.id) &&
        !this.disabledOptions.get(option.id) &&
        this.getOptionProperty(option, property) === value
    );
    const score = (option: ProductConfigurationOption) =>
      Object.keys(wanted).filter(p => this.getOptionProperty(option, p) === wanted[p]).length;
    const option = candidates.sort((a, b) => score(b) - score(a))[0];
    if (!option) {
      return;
    }
    this.pillSelections[feature.id] = this.getPillsOf(option);
    this.featureNgModels[feature.id] = option.id;
    this.featureOptionSelected(feature, option);
  }

  protected getPillsOf(option: ProductConfigurationOption | undefined): { [property: string]: string } {
    const pills = {};
    if (option) {
      this.cpqConfigService.getPillProperties().forEach(p => (pills[p] = this.getOptionProperty(option, p)));
    }
    return pills;
  }

  protected getOptionProperty(option: ProductConfigurationOption, property: string): string {
    const value = option?.properties?.[property];
    return value === undefined || value === null ? '' : String(value).trim();
  }

  hasOptionSelected(featureId: string, option: ProductConfigurationOption) {
    return this.featureResults.has(featureId) && !!this.featureResults.get(featureId).find(o => o.id == option.id);
  }

  isFeatureVisible(feature: ProductConfigurationFeature): boolean {
    const key = this.cpqConfigService.getFeatureVisibilityPropertyKey();
    if (key && !this.isFeaturePropertyTrue(feature, key)) {
      return false;
    }
    return !this.cpqConfigService.hideFeaturesWithoutOptions() || this.hasConnectedOptions(feature);
  }

  protected hasConnectedOptions(feature: ProductConfigurationFeature): boolean {
    const connected = this.configuration?.features?.find(f => f.featureId === feature?.id);
    return (connected?.optionIds?.length || 0) > 0;
  }

  protected isFeaturePropertyTrue(feature: ProductConfigurationFeature, key: string): boolean {
    const value = feature?.properties?.[key];
    return value === true || value === 'true';
  }

  isAllowedToOrder(): boolean {
    const allIds = this.configuration.features
      .map(f => f.featureId)
      .filter(id => this.isFeatureVisible(this.keyedFeatures.get(id)));
    const featuresRequiringAction = this.getFiltersRequiringAction(allIds);
    return this.featuresHasInputValues(featuresRequiringAction) && !this.hasClearedPill();
  }

  requiredFieldsInGroupHasActions(groupId: string): boolean {
    const featureIds = this.groupedFeatures.get(groupId).map(f => f.id);
    const featuresRequiringAction = this.getFiltersRequiringAction(featureIds);
    return this.featuresHasInputValues(featuresRequiringAction);
  }

  private featuresHasInputValues(featureIds: string[]) {
    for (const id of featureIds) {
      // Bad case, we have not received any input for this feature.
      if (!this.featureResults.has(id) || this.featureResults.get(id).length == 0) {
        return false;
      }
    }
    return true;
  }

  private getFiltersRequiringAction(featureIds: string[]): string[] {
    return featureIds
      .map(id => this.keyedFeatures.get(id))
      .filter(feature => {
        // Get optional from PIM. Fallback to not being optional.
        return (feature.properties?.optional || false) == false;
      })
      .map(feature => feature.id);
  }

  addToCart(): void {
    const productConfigurationSelectedOptions: CartRowFeatureOptionSelection[] = this.buildSelection();
    const extra = {
      productConfigurationSelectedOptions: this.filterSelectionsAddedAsAdditionalRows(
        productConfigurationSelectedOptions
      )
    };

    const cartRowsToAdd = [
      {
        productId: this.configurationOutputProductId || this.configuration.id,
        qty: this.getQuantity(),
        extra: extra,
        productPartialId: this.cpqConfigService.useProductPartialId() ? this.createProductPartialId() : undefined
      },
      ...this.createCartRowObjectsForAdditionalRows(productConfigurationSelectedOptions)
    ];

    if (this.editedCartRow) {
      const [configuredRow, ...additionalRows] = cartRowsToAdd;
      this.cartService.replaceCartRow(this.editedCartRow, {
        ...configuredRow,
        productPartialId: this.editedCartRow.productPartialId,
        targetStockId: this.editedCartRow.targetStockId,
        timeAdded: this.editedCartRow.timeAdded,
        timeUpdated: new Date()
      });
      if (additionalRows.length > 0) {
        this.cartService.bulkAddToCart(additionalRows);
      }
      this.editedCartRow = undefined;
    } else {
      this.cartService.bulkAddToCart(cartRowsToAdd);
    }
    this.gungNotificationService.notify('Added to cart', 'The configuration was added to the current cart', 'success');
  }

  protected getQuantity(): number {
    return Math.max(1, Math.floor(Number(this.quantity) || 1));
  }

  protected createProductPartialId(): string {
    return (this.configurationOutputProductId || this.configuration.id) + '@' + Date.now();
  }

  protected filterSelectionsAddedAsAdditionalRows(
    selections: CartRowFeatureOptionSelection[]
  ): CartRowFeatureOptionSelection[] {
    return selections.filter(selection => !this.isSelectionThatShouldBeAddedOnSeparateRow(selection));
  }

  private isSelectionThatShouldBeAddedOnSeparateRow(selection: CartRowFeatureOptionSelection): boolean {
    return !!selection.feature?.properties?.orderOnSeparateRow || false;
  }

  protected createCartRowObjectsForAdditionalRows(selections: CartRowFeatureOptionSelection[]): any {
    return (
      selections
        // We only care about the ones that should be on a separate row.
        .filter(selection => this.isSelectionThatShouldBeAddedOnSeparateRow(selection))
        .flatMap(selection => {
          // We need to create rows for each of the materials.
          return selection.option.materials.map(material => {
            return {
              productId: material.productId,
              // Material.quantity is the quantity required for each selection. If we have multiple of the selection we
              // need to multiply by that amount in order to get the total.
              qty: material.quantity * selection.qty * this.getQuantity()
            };
          });
        })
    );
  }

  buildSelection(): CartRowFeatureOptionSelection[] {
    const productConfigurationSelectedOptions: CartRowFeatureOptionSelection[] = [];
    if (!this.featureResults || this.featureResults.size == 0) {
      return productConfigurationSelectedOptions;
    }

    for (const featureId of this.featureResults.keys()) {
      const options = this.featureResults.get(featureId);
      if (options.length == 0) {
        continue;
      }

      const mappedOptions = options.map(option => {
        return {
          option: option,
          feature: this.keyedFeatures.get(featureId),
          qty: Number(option.properties?.quantityAdded) || 1
        };
      });

      productConfigurationSelectedOptions.push(...mappedOptions);
    }
    return productConfigurationSelectedOptions;
  }

  protected readonly FeatureOptionType = FeatureOptionType;

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  protected readonly String = String;
}
